// src/pages/HomePage/HomePage.jsx
import React, { useEffect, useState } from "react";
import { Search, X, PlusCircle } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import "./HomePage.css";
import { motion, AnimatePresence } from "framer-motion";
import { useCurrentMonthEvents } from "./hooks/useCurrentMonthEvents.js";
import { useLastMonthEvents } from "./hooks/useLastMonthEvents.js";
import { usePersonalEvents } from "./hooks/usePersonalEvents.js";
import { useCountryEvents } from "./hooks/useCountryEvents.js";
import EventHighlight from "./components/EventHighlight.jsx";
import PersonalEvents from "./components/PersonalEvents.jsx";
import HomeCountryEvents from "./components/HomeCountryEvents.jsx";
import CurrentMonthEvents from "./components/CurrentMonthEvents.jsx";
import LastMonthEvents from "./components/LastMonthEvents.jsx";
import AttendSection from "./components/AttendSection.jsx";

// ==================== SHARED STYLES ====================
const S = {
  page: {
    background: "var(--card-bg, rgb(4,4,4))",
    minHeight:  "100vh",
    color:      "white",
    marginTop:  0,
  },
  container: {
    width:     "80%",
    margin:    "0 auto",
    boxSizing: "border-box",
  },
  topBar: {
    display:        "flex",
    alignItems:     "center",
    justifyContent: "space-between",
    marginBottom:   "20px",
    flexWrap:       "nowrap",
    gap:            "12px",
    paddingTop:     "24px",
  },
  divider: {
    height:       "1px",
    background:   "var(--border-color, rgb(52,51,51))",
    marginBottom: "24px",
  },
  footer: {
    borderTop:  "1px solid var(--border-color, rgb(52,51,51))",
    padding:    "28px 16px",
    marginTop:  "48px",
    background: "var(--card-bg, rgb(4,4,4))",
  },
  footerInner: {
    width:     "80%",
    margin:    "0 auto",
    textAlign: "center",
    fontSize:  "13px",
    color:     "rgba(255,255,255,0.35)",
  },
  pillBtn: (active = false, danger = false) => ({
    display:      "flex",
    alignItems:   "center",
    gap:          "6px",
    padding:      "8px 14px",
    borderRadius: "8px",
    border:       "1px solid var(--border-color, rgb(52,51,51))",
    background:   active ? "rgba(255,255,255,0.12)" : "transparent",
    color:        danger ? "#ff5c5c" : active ? "white" : "rgba(255,255,255,0.85)",
    cursor:       "pointer",
    fontSize:     "14px",
    fontWeight:   active ? 600 : 500,
    transition:   "background 0.2s, color 0.2s",
    whiteSpace:   "nowrap",
  }),
  tabGroup: {
    display:      "flex",
    alignItems:   "center",
    gap:          "4px",
    background:   "rgba(255,255,255,0.04)",
    border:       "1px solid var(--border-color, rgb(52,51,51))",
    borderRadius: "10px",
    padding:      "4px",
  },
  tabBtn: (isActive) => ({
    display:      "flex",
    alignItems:   "center",
    gap:          "6px",
    padding:      "7px 14px",
    borderRadius: "7px",
    border:       "none",
    background:   isActive ? "rgba(255,255,255,0.12)" : "transparent",
    color:        isActive ? "white" : "rgba(255,255,255,0.5)",
    cursor:       "pointer",
    fontSize:     "13px",
    fontWeight:   isActive ? 600 : 400,
    transition:   "background 0.2s, color 0.2s",
    whiteSpace:   "nowrap",
  }),
  sectionTitle: {
    fontSize:      "15px",
    fontWeight:    600,
    color:         "rgba(255,255,255,0.9)",
    marginBottom:  "16px",
    marginTop:     0,
    letterSpacing: "0.01em",
  },
  addEventBtn: {
    display:      "flex",
    alignItems:   "center",
    gap:          "6px",
    padding:      "9px 18px",
    borderRadius: "8px",
    border:       "1px solid var(--border-color, rgb(52,51,51))",
    background:   "transparent",
    color:        "rgba(255,255,255,0.85)",
    cursor:       "pointer",
    fontSize:     "14px",
    fontWeight:   500,
    transition:   "background 0.2s, color 0.2s",
  },
  searchIconBtn: {
    display:        "flex",
    alignItems:     "center",
    justifyContent: "center",
    gap:            "6px",
    padding:        "8px 14px",
    borderRadius:   "8px",
    border:         "1px solid var(--border-color, rgb(52,51,51))",
    background:     "transparent",
    color:          "rgba(255,255,255,0.85)",
    cursor:         "pointer",
    fontSize:       "14px",
    fontWeight:     500,
    transition:     "background 0.2s, color 0.2s",
    whiteSpace:     "nowrap",
    flexShrink:     0,
  },
};

// // ==================== TABS ====================
const TABS = [
  {
    id: "highlight",
    label: "Highlight",
    description: "Everything on the calendar right now, front and centre",
  },
  {
    id: "attend",
    label: "Attend",
    description: "Events scheduled near your current location",
  },
  {
    id: "watch",
    label: "Watch",
    description: "Happening live on the Kleido platform right now",
  },
  {
    id: "learn",
    label: "Learn",
    description: "Culture and heritage, explained by the people who keep it",
  },
  {
    id: "taste",
    label: "Taste",
    description: "Food festivals and tasting events on the calendar",
  },
  {
    id: "participate",
    label: "Participate",
    description: "National and cultural public festivals open to everyone",
  },
  {
    id: "visit",
    label: "Visit",
    description: "Scheduled for the next few months — plan ahead",
    comingSoon: true,
  },
  {
    id: "create",
    label: "Create",
    description: "Start your own event and invite your people",
  },
  {
    id: "volunteer",
    label: "Volunteer",
    description: "Events looking for hands, not just attendance",
  },
  {
    id: "celebrate",
    label: "Celebrate",
    description: "Mark the occasion wherever you are — no attendance needed",
  },
  {
    id: "explore",
    label: "Explore",
    description: "New formats and events you haven't come across yet",
  },
];

// ==================== TAB BAR ====================

const TabBar = ({ activeTab, onChange }) => (
  <div style={{ ...S.tabGroup, overflowX: "auto" }}>
    {TABS.map((tab) => (
      <button
        key={tab.id}
        onClick={() => onChange(tab.id)}
        aria-pressed={activeTab === tab.id}
        title={tab.description}
        style={S.tabBtn(activeTab === tab.id)}
      >
        {tab.label}
        {tab.comingSoon && (
          <span style={{
            fontSize: "8px",
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.35)",
            border: "1px solid rgba(255,255,255,0.15)",
            borderRadius: "999px",
            padding: "1px 5px",
          }}>
            Soon
          </span>
        )}
      </button>
    ))}
  </div>
);

// Feature codes seeded on entry so the product dropdown sees every product.
const FEATURE_STORAGE_KEY = "kleido-selected-features";
const ALL_FEATURES = ["KE", "KW", "KR", "KG", "KC", "KP", "KA"];

// ==================== SECTION WRAPPER ====================

const Section = ({ children, style = {} }) => (
  <div style={{ marginBottom: "32px", ...style }}>
    {children}
    <div style={{ ...S.divider, marginTop: "32px", marginBottom: 0 }} />
  </div>
);

// ==================== FEED SEARCH MODAL ====================

const COUNTRIES = [
  "India", "United States", "United Kingdom", "Australia",
  "Canada", "Singapore", "United Arab Emirates", "Germany",
];

const YEARS = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 2 + i);

const FeedSearchModal = ({ onClose, onSubmit }) => {
  const [country, setCountry] = useState("");
  const [event,   setEvent]   = useState("");
  const [year,    setYear]    = useState("");

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const field = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: "8px",
    border: "1px solid var(--border-color, rgb(52,51,51))",
    background: "rgba(255,255,255,0.05)",
    color: "white",
    fontSize: "14px",
    outline: "none",
  };

  const label = {
    display: "block",
    marginBottom: "6px",
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.4)",
  };

  const canSubmit = country || event || year;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,0.72)",
        backdropFilter: "blur(6px)",
      }}
    >
      <motion.div
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, y: 14, scale: 0.97 }}
        animate={{ opacity: 1, y: 0,  scale: 1    }}
        exit={{    opacity: 0, y: 14, scale: 0.97 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        role="dialog"
        aria-modal="true"
        aria-label="Search events"
        style={{
          width: "min(440px, 90vw)",
          padding: "26px",
          borderRadius: "16px",
          background: "rgb(12,12,12)",
          border: "1px solid var(--border-color, rgb(52,51,51))",
          boxShadow: "0 30px 80px rgba(0,0,0,0.7)",
        }}
      >
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "22px",
        }}>
          <h3 style={{ margin: 0, fontSize: "17px", fontWeight: 600, color: "white" }}>
            Find events
          </h3>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              display: "flex",
              padding: "6px",
              borderRadius: "7px",
              border: "none",
              background: "transparent",
              color: "rgba(255,255,255,0.5)",
              cursor: "pointer",
            }}
          >
            <X size={16} />
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div>
            <label style={label} htmlFor="fs-country">Country</label>
            <select
              id="fs-country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              style={field}
            >
              <option value="">Any country</option>
              {COUNTRIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={label} htmlFor="fs-event">Event</label>
            <input
              id="fs-event"
              type="text"
              value={event}
              onChange={(e) => setEvent(e.target.value)}
              placeholder="Diwali, Holi, Navratri…"
              style={field}
            />
          </div>

          <div>
            <label style={label} htmlFor="fs-year">Year</label>
            <select
              id="fs-year"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              style={field}
            >
              <option value="">Any year</option>
              {YEARS.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>

        <button
          onClick={() => onSubmit({ country, event: event.trim(), year })}
          disabled={!canSubmit}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            width: "100%",
            marginTop: "24px",
            padding: "12px",
            borderRadius: "8px",
            border: "1px solid var(--border-color, rgb(52,51,51))",
            background: canSubmit ? "rgba(255,255,255,0.12)" : "transparent",
            color: canSubmit ? "white" : "rgba(255,255,255,0.3)",
            fontSize: "14px",
            fontWeight: 600,
            cursor: canSubmit ? "pointer" : "not-allowed",
          }}
        >
          <Search size={15} />
          Show feed
        </button>

        <p style={{
          margin: "12px 0 0",
          fontSize: "11px",
          textAlign: "center",
          color: "rgba(255,255,255,0.3)",
        }}>
          Fill any one field. Results open as a media feed.
        </p>
      </motion.div>
    </motion.div>
  );
};


// ==================== MAIN COMPONENT ====================

const HomePage = () => {
  const { userid: urlUserid } = useParams();
  const navigate = useNavigate();

  const [isLoggedIn,      setIsLoggedIn]      = useState(false);
  const [userid,          setUserid]          = useState(null);
  const [countryid,       setCountryid]       = useState(null);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [activeTab, setActiveTab] = useState("highlight");

  const activeTabMeta = TABS.find((t) => t.id === activeTab) ?? TABS[0];

  // ==================== CHECK LOCALSTORAGE ====================
  useEffect(() => {
    try {
      const raw = localStorage.getItem("userLoggedin");
      if (raw) {
        const parsed  = JSON.parse(raw);
        const id      = parsed.userid || parsed.userId || parsed.id || null;
        const country = parsed.country || null;
        if (id) {
          setIsLoggedIn(true);
          setUserid(id);
          setCountryid(country);
          console.log("✅ [HomePage] Logged-in user from localStorage:", id);
        } else {
          console.warn("⚠️ [HomePage] userLoggedin exists but has no userId");
          setIsLoggedIn(false);
          setUserid(urlUserid || null);
        }
      } else {
        console.log("ℹ️ [HomePage] No userLoggedin in localStorage");
        setIsLoggedIn(false);
        setUserid(urlUserid || null);
      }
    } catch (err) {
      console.error("❌ [HomePage] Failed to parse userLoggedin:", err);
      setIsLoggedIn(false);
      setUserid(urlUserid || null);
    }
  }, [urlUserid]);

  // ==================== EVENT HOOKS ====================
  const { highlightEvent, monthlyEvents, loading: currentLoading } = useCurrentMonthEvents();
  const { lastEvents,     loading: lastLoading }                    = useLastMonthEvents();
  const { personalEvents, loading: personalLoading }                = usePersonalEvents(userid, isLoggedIn);
  const { countryEvents,  loading: eventLoading }                   = useCountryEvents(userid, countryid, isLoggedIn);

  // ==================== AUTO-CREATE USER ====================
  useEffect(() => {
    try {
      const usersString = localStorage.getItem("users");
      let users = [];
      try { users = usersString ? JSON.parse(usersString) : []; } catch { users = []; }

      const targetUserid = urlUserid || "sample";
      const userExists   = users.some((u) => u.userId === targetUserid);

      if (!userExists) {
        const newUser = {
          userId:    targetUserid,
          email:     `${targetUserid}@initiumnet.com`,
          mobile:    "0000000000",
          password:  "password",
          name:      targetUserid === "sample" ? "Sample User" : targetUserid,
          createdAt: new Date().toISOString(),
          country:   null,
          features:  [],
        };
        users.push(newUser);
        localStorage.setItem("users", JSON.stringify(users));
        console.log("✅ [HomePage] Auto-created user:", targetUserid);

        if (urlUserid && urlUserid !== "sample") {
          const userLoggedIn = {
            userid:     targetUserid,
            email:      newUser.email,
            mobile:     newUser.mobile,
            name:       newUser.name,
            loggedInAt: new Date().toISOString(),
          };
          localStorage.setItem("userLoggedin", JSON.stringify(userLoggedIn));
          localStorage.setItem("status", "loggedin");
          setIsLoggedIn(true);
          setUserid(targetUserid);
          window.dispatchEvent(new Event("storage"));
          console.log("🔐 [HomePage] Auto-logged in:", targetUserid);
        }
      }
    } catch (err) {
      console.error("❌ [HomePage] Auto-create error:", err);
    }
  }, [urlUserid]);

  // ==================== DOUBLE-CLICK SEARCH ====================
  useEffect(() => {
    const handleDoubleClick = (e) => {
      if (
        e.target.tagName === "BUTTON" ||
        e.target.tagName === "A"      ||
        e.target.tagName === "INPUT"  ||
        e.target.closest("button")    ||
        e.target.closest("a")
      ) return;
      setShowSearchModal(true);
    };
    document.addEventListener("dblclick", handleDoubleClick);
    return () => document.removeEventListener("dblclick", handleDoubleClick);
  }, []);

    // ==================== SEED FEATURE FLAGS ====================
    useEffect(() => {
      try {
        localStorage.setItem(FEATURE_STORAGE_KEY, JSON.stringify(ALL_FEATURES));
        console.log("✅ [HomePage] Seeded features:", ALL_FEATURES);
      } catch (err) {
        console.error("❌ [HomePage] Failed to seed feature flags:", err);
      }
    }, []);

  // ==================== SEARCH HANDLERS ====================
  const handleFeedSearch = ({ country, event, year }) => {
    const effectiveUserid = userid || "sample";
    const params = new URLSearchParams();
    if (country) params.set("country", country);
    if (event)   params.set("event", event);
    if (year)    params.set("year", year);

    navigate(`/event-feed/${effectiveUserid}?${params.toString()}`);
    setShowSearchModal(false);
  };

  // ==================== ADD EVENT HANDLER ====================
  // ✅ Navigates to /post-content/:userid
  // groupid is optional per the route definition, so we only pass userid here.
  const handleAddEvent = () => {
    const effectiveUserid = userid || "sample";
    navigate(`/post-content/${effectiveUserid}`);
  };

  // ==================== LOADING STATE ====================
  if (currentLoading && !highlightEvent) {
    return (
      <div style={{
        ...S.page, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", gap: "16px",
      }}>
        <div className="loading-spinner" />
        <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "14px", margin: 0 }}>
          Loading events...
        </p>
      </div>
    );
  }

  // ==================== RENDER ====================
  return (
    <div style={S.page}>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0  }}
        transition={{ duration: 0.4, ease: "easeOut" }}
      >
        {/* ── Central 80% Container ─────────────────────── */}
        <div style={S.container}>

          {/* ── Top Bar ────────────────────────────────── */}
          <div style={S.topBar}>
            {/* Left: Category tabs */}
            <div style={{ minWidth: 0, overflowX: "auto", display: "flex" }}>
              <TabBar activeTab={activeTab} onChange={setActiveTab} />
            </div>

            {/* Right: Search */}
            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
              <button
                onClick={() => setShowSearchModal(true)}
                aria-label="Search events"
                style={S.searchIconBtn}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(255,255,255,0.07)";
                  e.currentTarget.style.color = "white";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.color = "rgba(255,255,255,0.85)";
                }}
              >
                <Search size={15} />
                Search
              </button>
            </div>
          </div>

          {/* ── Divider ─────────────────────────────────── */}
          <div style={S.divider} />
          {activeTab === "attend" && (
            <AttendSection
              userid={userid || "sample"}
              onOpenEvent={(ev) =>
                navigate(`/event-details/${userid || "sample"}/${ev.eventid}`)
              }
            />
              // <div>attend placeholder</div>
          )}

          {/* ── HIGHLIGHT ───────────────────────────────── */}
          {activeTab === "highlight" && (
            <>
              {highlightEvent && (
                <Section>
                  <EventHighlight event={highlightEvent} />
                </Section>
              )}

              {isLoggedIn && userid && (
                <Section>
                  <PersonalEvents
                    events={personalEvents}
                    loading={personalLoading}
                    isLoggedIn={isLoggedIn}
                    userid={userid}
                    country={countryid}
                  />
                </Section>
              )}

              {isLoggedIn && userid && (
                <Section>
                  <HomeCountryEvents
                    events={countryEvents}
                    loading={eventLoading}
                    isLoggedIn={isLoggedIn}
                    userid={userid}
                  />
                </Section>
              )}

              {(monthlyEvents.length > 0 || lastEvents.length > 0) && (
                <>
                  <Section>
                    <CurrentMonthEvents events={monthlyEvents} loading={currentLoading} />
                  </Section>

                  <Section>
                    <LastMonthEvents events={lastEvents} loading={lastLoading} />
                  </Section>
                </>
              )}
            </>
          )}

          {/* ── EVERYTHING ELSE — placeholder ───────────── */}
          {activeTab !== "highlight" && activeTab !== "attend" && (
            <div style={{
              padding: "80px 20px",
              textAlign: "center",
              color: "rgba(255,255,255,0.35)",
              fontSize: "14px",
            }}>
              <div style={{ fontSize: "15px", fontWeight: 600, color: "rgba(255,255,255,0.6)", marginBottom: "8px" }}>
                {activeTabMeta.label}
              </div>
              {activeTabMeta.description}
              <div style={{ marginTop: "16px", fontSize: "12px", color: "rgba(255,255,255,0.25)" }}>
                Coming soon
              </div>
            </div>
          )}

          {/* ── Add Event ───────────────────────────────── */}
          {/* ✅ UPDATED: onClick now navigates to /post-content/:userid */}
          <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: "48px" }}>
            <button
              style={S.addEventBtn}
              onClick={handleAddEvent}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.07)";
                e.currentTarget.style.color      = "white";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color      = "rgba(255,255,255,0.85)";
              }}
            >
              <PlusCircle size={15} />
              Add Event
            </button>
          </div>

        </div>{/* /80% container */}

        {/* ── Active tab description ──────────────────── */}
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "18px",
            fontSize: "13px",
            color: "rgba(255,255,255,0.4)",
          }}>
            <span style={{ color: "rgba(255,255,255,0.85)", fontWeight: 600 }}>
              {activeTabMeta.label}
            </span>
            <span>— {activeTabMeta.description}</span>
            {activeTabMeta.comingSoon && (
              <span style={{
                fontSize: "10px",
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.4)",
                border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: "999px",
                padding: "2px 8px",
              }}>
                Coming soon
              </span>
            )}
          </div>
      </motion.div>

      {/* ── Footer ──────────────────────────────────────── */}
      <footer style={S.footer}>
        <div style={S.footerInner}>
          <p style={{ margin: 0 }}>
            © 2025 Kleido AI. An Initiumnet Company.
          </p>
        </div>
      </footer>

      {/* ── Legacy double-click Search Modal ────────────── */}
      <AnimatePresence>
        {showSearchModal && (
          <FeedSearchModal
            onClose={() => setShowSearchModal(false)}
            onSubmit={handleFeedSearch}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default HomePage;
